import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography, CircularProgress, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import { getTimelineByProductId } from "../../api/Estimates/estimatesApi";
import { useTheme } from "@mui/material/styles";
import { applyGridSnapshotToTimeline } from "./criticalPathTimelineUtils";

import {
  ChatBubbleOutline,
  LocalShippingOutlined,
  FactoryOutlined,
  ScienceOutlined,
  CheckCircleOutline,
  SearchOutlined,
  EventOutlined,
  DescriptionOutlined,
  RequestQuoteOutlined,
  Inventory2Outlined,
  AssignmentTurnedInOutlined,
  BookmarkBorderOutlined,
  StoreOutlined,
  DoneAllOutlined,
  CalendarMonthOutlined,
  RadioButtonUnchecked,
} from "@mui/icons-material";

const ROW_HEIGHT = 24;

const clientColors = [
  "#00C2A3",
  "#42A5F5",
  "#AB47BC",
  "#26A69A",
  "#FFA726",
  "#EF5350",
  "#66BB6A",
  "#8D6E63",
  "#5C6BC0",
];

const CriticalPathInlineGanttAligned = ({
  productId,
  idPresupuesto = null,
  idPresupuestoA3Pedido = null,
  clientName,
  scaleX,
  startMin,
  endMax,
  onHeightChange,
  onTimelineLoaded,
  refreshKey = 0,
  /** Si true: solo timeline por pedido A3 (fila ya migrada). */
  hideLegacyTimeline = false,
  /** Si true: solo timeline legacy (fila sin pedido A3). */
  preferLegacyTimeline = false,
  /** Fila del grid padre: fallback si el API filtrado queda vacío. */
  gridRowSnapshot = null,
}) => {
  const theme = useTheme();
  const [timeline, setTimeline] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const today = Date.now();

  const timelinePresupuestoOpts = useMemo(() => {
    if (
      idPresupuestoA3Pedido != null &&
      Number.isFinite(Number(idPresupuestoA3Pedido))
    ) {
      return { idPresupuestoA3Pedido: Number(idPresupuestoA3Pedido) };
    }
    if (idPresupuesto != null && Number.isFinite(Number(idPresupuesto))) {
      return { idPresupuesto: Number(idPresupuesto) };
    }
    return {};
  }, [idPresupuesto, idPresupuestoA3Pedido]);

  // 🟩 Calcula el mismo color que usa el padre
  const baseColor = React.useMemo(() => {
    if (!clientName) return "#42A5F5";
    const sum = Array.from(clientName || "")
      .map((c) => c.charCodeAt(0))
      .reduce((a, b) => a + b, 0);
    return clientColors[Math.abs(sum) % clientColors.length];
  }, [clientName]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);

        // 1️⃣ Cargar timeline normal
        const rawTimeline = await getTimelineByProductId(
          productId,
          timelinePresupuestoOpts,
        );
        let timelineData = applyGridSnapshotToTimeline(
          rawTimeline,
          gridRowSnapshot,
          false,
          {
            hideLegacyTimeline,
            preferLegacyTimeline,
            idPresupuestoA3Pedido,
          },
        );

        // 2️⃣ Buscar la task "Shipment Method" para saber su ID
        const shipmentTask = timelineData.find(
          (t) =>
            String(t?.TaskName ?? t?.taskname ?? "")
              .trim()
              .toLowerCase() === "shipment method",
        );

        // 3️⃣ Si existe, cargar logística asociada
        let logisticsData = [];
        if (shipmentTask) {
          const res = await fetch(
            `${process.env.REACT_APP_API_URL}/api/logistics/${shipmentTask.Id}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          logisticsData = await res.json();
        }

        // 4️⃣ Convertir los campos de logística a pseudo-tareas
        const extraTasks = logisticsData.flatMap((entry) => {
          const mapDate = (label, value) =>
            value
              ? {
                  TaskName: label,
                  GroupName: "Logistic",
                  ExpectedDate: null,
                  UpdatedDate: value,
                  RealDate: null,
                  Id: `${entry.Id}-${label}`,
                }
              : null;

          return [
            mapDate("PO Date", entry.PODate),
            mapDate("Booking Done", entry.BookingDone),
            mapDate("Documents Approved", entry.DocumentsApproved),
            mapDate("Telex Docs", entry.OriginalTelexDocsSemt),
            mapDate("ETA", entry.ETA),
          ].filter(Boolean);
        });

        // 5️⃣ Fusionar timeline + logística
        const fullTimeline = [...timelineData, ...extraTasks];
        setTimeline(fullTimeline);

        if (onTimelineLoaded) onTimelineLoaded(fullTimeline);
      } catch (err) {
        console.error("❌ Error cargando timeline con logística:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (productId) loadData();
  }, [
    productId,
    refreshKey,
    timelinePresupuestoOpts,
    hideLegacyTimeline,
    preferLegacyTimeline,
    idPresupuestoA3Pedido,
    gridRowSnapshot,
  ]);

  useEffect(() => {
    if (!isLoading && timeline.length && typeof onHeightChange === "function") {
      onHeightChange(timeline.length * ROW_HEIGHT);
    }
  }, [isLoading, timeline, onHeightChange]);

  if (isLoading)
    return (
      <Box
        sx={{
          height: 120,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          /* background: theme.palette.background.paper, */
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );

  if (!timeline.length)
    return (
      <Typography sx={{ p: 2, fontSize: ".8rem" }}>
        No hay tareas para este producto.
      </Typography>
    );

  const totalHeight = timeline.length * ROW_HEIGHT;

  // 🧭 Mapa de iconos por tipo de tarea
  const ICON_MAP = {
    // 📞 Comunicación
    "SMS comments": ChatBubbleOutline,
    "SSS Fitting comments": ChatBubbleOutline,

    // 🧵 Envíos y fittings
    "SSS Fitting send": LocalShippingOutlined,
    "PP send": LocalShippingOutlined,
    "TOP sple send": LocalShippingOutlined,
    "TOP approved": CheckCircleOutline,

    // 🏭 Producción y materiales
    "Fabrics and trims In-house": FactoryOutlined,
    "Production Starts": FactoryOutlined,
    "Ok production Received": DoneAllOutlined,

    // 🔬 Laboratorio / pruebas
    "Testing send to lab": ScienceOutlined,

    // 🔍 Control e inspección
    "Inspection date": SearchOutlined,

    // 📆 Fechas clave
    ExFD: EventOutlined,
    ETDF: EventOutlined,
    "CUT-OFF": EventOutlined,
    ETD: EventOutlined,
    DD: EventOutlined,
    ETA: EventOutlined,

    // 📦 Logística
    "N Cartons": Inventory2Outlined,
    "Gross Weight Kgs": Inventory2Outlined,
    "Total Volume cbm": Inventory2Outlined,
    "Shipment Method": LocalShippingOutlined,
    Forwarder: LocalShippingOutlined,
    "Booking Done": BookmarkBorderOutlined,
    "Shipment Code Customer": DescriptionOutlined,
    "BL AWB Nr": DescriptionOutlined,

    // 📄 Documentación y aprobación
    "Documents Approved": AssignmentTurnedInOutlined,
    "Original Telex Docs Semt": DescriptionOutlined,

    // 💰 Finanzas / órdenes de compra
    POReference: RequestQuoteOutlined,
    PODate: CalendarMonthOutlined,
    "PO Date": CalendarMonthOutlined,

    // 🏭 Fábricas
    Factory: StoreOutlined,
  };

  return (
    <svg
      width="100%"
      height={totalHeight}
      style={{
        background: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Fondo alterno */}
      {timeline.map((t, i) => (
        <rect
          key={`bg-${i}`}
          x="0"
          y={i * ROW_HEIGHT}
          width="100%"
          height={ROW_HEIGHT}
          fill={
            i % 2 === 0
              ? theme.palette.background.default
              : theme.palette.background.paper
          }
        />
      ))}

      {/* 🟠 Banda de fondo para la sección logística */}
      {(() => {
        const logisticIndices = timeline
          .map((t, i) => (t.GroupName === "Logistic" ? i : -1))
          .filter((i) => i !== -1);

        if (!logisticIndices.length) return null;

        const yStart = logisticIndices[0] * ROW_HEIGHT;
        const yEnd =
          logisticIndices[logisticIndices.length - 1] * ROW_HEIGHT + ROW_HEIGHT;

        return (
          <rect
            x={0}
            y={yStart}
            width="100%"
            height={yEnd - yStart}
            fill={theme.palette.background.paper}
            opacity={0.8}
          />
        );
      })()}

      {/* 🔴 Línea HOY */}
      {today >= startMin && today <= endMax && (
        <>
          <line
            x1={scaleX(today)}
            x2={scaleX(today)}
            y1={0}
            y2={totalHeight}
            stroke="#ff6b6b"
            strokeDasharray="4 3"
            strokeWidth="1.5"
          />
          {/*  <rect
            x={scaleX(today) - 14}
            y={4}
            width="40"
            height="16"
            fill="rgba(255,255,255,0.9)"
            rx="3"
          />
          <text
            x={scaleX(today) + 5}
            y={16}
            fill="#e53935"
            fontWeight="bold"
            fontSize="11"
          >
            HOY
          </text> */}
        </>
      )}

      {/* Barras y puntos */}
      {timeline.map((t, i) => {
        const y = i * ROW_HEIGHT + ROW_HEIGHT / 2;
        const expected = dayjs(t.ExpectedDate);
        const updated = dayjs(t.UpdatedDate);
        const real = dayjs(t.RealDate);
        if (!expected.isValid()) return null;

        const xStart = scaleX(expected.valueOf());
        const xEnd = real.isValid()
          ? scaleX(real.valueOf())
          : updated.isValid()
          ? scaleX(updated.valueOf())
          : xStart + 8;

        // usa el color del cliente para la barra
        const barColor = baseColor;

        // usa la misma lógica que el padre para las bolitas
        // 🧠 Color dinámico según fecha esperada vs hoy
        let dotColor = "#00c853"; // verde por defecto
        if (expected.isValid()) {
          const diffDays = expected.diff(dayjs(), "day");
          if (diffDays < 0) dotColor = "#9e9e9e"; // vencido
          else if (diffDays === 0) dotColor = "#ff1744"; // hoy
          else if (diffDays <= 5) dotColor = "#e53935"; // ≤5 días
          else if (diffDays <= 10) dotColor = "#ff9800"; // ≤10 días
          else dotColor = "#00c853"; // >10 días
        }

        return (
          <g key={t.Id}>
            {/* barra principal */}
            <line
              x1={xStart}
              x2={xEnd}
              y1={y}
              y2={y}
              stroke={barColor}
              strokeWidth="5"
              strokeLinecap="round"
            />

            {/* bolita inicio */}
            {/* <circle cx={xStart} cy={y} r={4} fill={dotColor} /> */}

            {/* bolita fin */}
            {/* {real.isValid() && (
              <circle
                cx={xEnd}
                cy={y}
                r={4}
                fill="#00c853"
                stroke="#222"
                strokeWidth="0.6"
              />
            )} */}

            <circle cx={xEnd} cy={y} r={4} fill={dotColor} opacity={0.25} />
            <foreignObject x={xEnd - 8} y={y - 10} width={16} height={16}>
              {(() => {
                const IconEl = ICON_MAP[t.TaskName] || RadioButtonUnchecked;
                return <IconEl sx={{ fontSize: 14, color: dotColor }} />;
              })()}
            </foreignObject>

            <Tooltip
              title={
                <>
                  <Typography fontWeight={600} fontSize={11}>
                    {t.TaskName}
                  </Typography>
                  <Typography variant="caption">
                    Exp: {expected.isValid() ? expected.format("DD/MM") : "—"}
                  </Typography>
                  <br />
                  <Typography variant="caption">
                    Real: {real.isValid() ? real.format("DD/MM") : "—"}
                  </Typography>
                </>
              }
              arrow
            >
              <rect
                x={Math.min(xStart, xEnd)}
                y={y - ROW_HEIGHT / 2}
                width={Math.abs(xEnd - xStart)}
                height={ROW_HEIGHT}
                fill="transparent"
              />
            </Tooltip>
          </g>
        );
      })}
      {/* 🔶 LOGÍSTICA: pintar bolas aunque no haya ExpectedDate */}
      {timeline.map((t, i) => {
        const logisticTasks = [
          "Booking Done",
          "PODate",
          "PO Reference",
          "Shipment Method",
          "Shipment Code Customer",
          "Forwarder",
          "BL AWB Nr",
          "ETA",
          "Documents Approved",
          "Original Telex Docs Semt",
        ];

        if (t.GroupName !== "Logistic" && !logisticTasks.includes(t.TaskName))
          return null;

        const y = i * ROW_HEIGHT + ROW_HEIGHT / 2;
        const updated = dayjs(t.UpdatedDate);
        const real = dayjs(t.RealDate);

        // pintar si hay Updated o Real aunque no haya Expected
        if (!updated.isValid() && !real.isValid()) return null;

        const pts = [];
        if (updated.isValid()) {
          pts.push({
            x: scaleX(updated.valueOf()),
            color: "#2196f3",
            label: "Updated",
          });
        }
        if (real.isValid()) {
          pts.push({
            x: scaleX(real.valueOf()),
            color: "#00c853",
            label: "Real",
          });
        }

        const logisticBar = theme.palette.background.paper;

        return (
          <g key={`log-${t.Id}`}>
            {updated.isValid() && real.isValid() && (
              <line
                x1={scaleX(updated.valueOf())}
                x2={scaleX(real.valueOf())}
                y1={y}
                y2={y}
                stroke={logisticBar}
                strokeWidth="4"
                strokeLinecap="round"
              />
            )}

            {pts.map((p, idx) => (
              <Tooltip
                key={`${t.Id}-log-${idx}`}
                title={
                  <>
                    <Typography fontWeight={600} fontSize={11}>
                      {t.TaskName}
                    </Typography>
                    {updated.isValid() && (
                      <Typography variant="caption">
                        Updated: {updated.format("DD/MM")}
                      </Typography>
                    )}
                    {real.isValid() && (
                      <>
                        <br />
                        <Typography variant="caption">
                          Real: {real.format("DD/MM")}
                        </Typography>
                      </>
                    )}
                  </>
                }
                arrow
              >
                <circle
                  cx={p.x}
                  cy={y}
                  r={3.8}
                  fill={p.color}
                  stroke="#222"
                  strokeWidth="0.6"
                />
              </Tooltip>
            ))}
          </g>
        );
      })}
    </svg>
  );
};

export default CriticalPathInlineGanttAligned;
